import type { Color } from '../../../types/theme';
import { rgba } from '../../../utils/colors';
import { backgroundComplementary } from './background-complementary';
import { borderComplementary } from './border-complementary';
import { graphics } from './graphics';

const color: Color = {
  background: {
    accent: {
      active: opacity => rgba(82, 95, 255, opacity),
      default: opacity => rgba(82, 95, 255, opacity),
      disabled: opacity => rgba(166, 172, 252, opacity),
      hover: opacity => rgba(34, 26, 178, opacity),
      contrast: opacity => rgba(34, 26, 178, opacity),
    },
    complementary: (color, opacity) => backgroundComplementary[color](opacity),
    danger: {
      active: opacity => rgba(255, 76, 76, opacity),
      default: opacity => rgba(255, 76, 76, opacity),
      disabled: opacity => rgba(237, 184, 184, opacity),
      hover: opacity => rgba(209, 47, 47, opacity),
    },
    error: {
      default: opacity => rgba(254, 219, 220, opacity),
    },
    graphics: (color, opacity) => graphics[color](opacity),
    info: {
      default: opacity => rgba(220, 223, 254, opacity),
    },
    premium: {
      active: opacity => rgba(242, 192, 56, opacity),
      default: opacity => rgba(242, 192, 56, opacity),
      disabled: opacity => rgba(192, 194, 204, opacity),
      hover: opacity => rgba(249, 178, 36, opacity),
    },
    primary: {
      active: opacity => rgba(242, 244, 252, opacity),
      contrast: opacity => rgba(255, 255, 255, opacity),
      default: opacity => rgba(255, 255, 255, opacity),
      disabled: opacity => rgba(242, 244, 252, opacity),
      hover: opacity => rgba(242, 244, 252, opacity),
      disabledContrast: opacity => rgba(192, 194, 204, opacity),
    },
    quaternary: {
      active: opacity => rgba(82, 95, 255, opacity),
      default: opacity => rgba(242, 244, 252, opacity),
      disabled: opacity => rgba(242, 244, 252, opacity),
      hover: opacity => rgba(224, 226, 235, opacity),
    },
    reversed: {
      active: opacity => rgba(0, 15, 51, opacity),
      default: opacity => rgba(0, 15, 51, opacity),
      disabled: opacity => rgba(224, 226, 235, opacity),
      hover: opacity => rgba(105, 107, 116, opacity),
    },
    secondary: {
      default: opacity => rgba(224, 226, 235, opacity),
      disabled: opacity => rgba(224, 226, 235, opacity),
      hover: opacity => rgba(192, 194, 204, opacity),
      active: opacity => rgba(82, 95, 255, opacity),
    },
    success: {
      default: opacity => rgba(229, 250, 244, opacity),
    },
    tertiary: {
      active: opacity => rgba(192, 194, 204, opacity),
      default: opacity => rgba(192, 194, 204, opacity),
      disabled: opacity => rgba(224, 226, 235, opacity),
      hover: opacity => rgba(105, 107, 116, opacity),
    },
    warning: {
      default: opacity => rgba(252, 242, 216, opacity),
    },
  },
  content: {
    ai: {
      active: opacity => rgba(247, 60, 105, opacity),
      default: opacity => rgba(247, 60, 105, opacity),
      disabled: opacity => rgba(247, 60, 105, opacity),
      hover: opacity => rgba(247, 60, 105, opacity),
    },
    accent: {
      active: opacity => rgba(82, 95, 255, opacity),
      default: opacity => rgba(82, 95, 255, opacity),
      disabled: opacity => rgba(220, 223, 254, opacity),
      hover: opacity => rgba(34, 26, 178, opacity),
    },
    error: {
      default: opacity => rgba(255, 76, 76, opacity),
      contrast: opacity => rgba(128, 46, 64, opacity),
      hoverContrast: opacity => rgba(0, 15, 51, opacity),
    },
    favorited: {
      active: opacity => rgba(249, 105, 215, opacity),
      default: opacity => rgba(249, 105, 215, opacity),
      disabled: opacity => rgba(192, 194, 204, opacity),
      hover: opacity => rgba(255, 158, 232, opacity),
    },
    info: {
      default: opacity => rgba(82, 95, 255, opacity),
      contrast: opacity => rgba(34, 26, 178, opacity),
      hoverContrast: opacity => rgba(0, 15, 51, opacity),
    },
    premium: {
      active: opacity => rgba(242, 192, 56, opacity),
      default: opacity => rgba(242, 192, 56, opacity),
      disabled: opacity => rgba(192, 194, 204, opacity),
      hover: opacity => rgba(249, 178, 36, opacity),
    },
    primary: {
      active: opacity => rgba(0, 15, 51, opacity),
      default: opacity => rgba(0, 15, 51, opacity),
      disabled: opacity => rgba(192, 194, 204, opacity),
      hover: opacity => rgba(0, 15, 51, opacity),
    },
    reversed: {
      active: opacity => rgba(255, 255, 255, opacity),
      default: opacity => rgba(255, 255, 255, opacity),
      disabled: opacity => rgba(255, 255, 255, opacity),
      hover: opacity => rgba(192, 194, 204, opacity),
    },
    secondary: {
      active: opacity => rgba(0, 15, 51, opacity),
      default: opacity => rgba(105, 107, 116, opacity),
      disabled: opacity => rgba(192, 194, 204, opacity),
      hover: opacity => rgba(0, 15, 51, opacity),
    },
    success: {
      default: opacity => rgba(20, 184, 125, opacity),
      contrast: opacity => rgba(17, 135, 88, opacity),
    },
    tertiary: {
      active: opacity => rgba(255, 255, 255, opacity),
      default: opacity => rgba(192, 194, 204, opacity),
      disabled: opacity => rgba(192, 194, 204, opacity),
      hover: opacity => rgba(105, 107, 116, opacity),
    },
    warning: {
      default: opacity => rgba(242, 192, 56, opacity),
      contrast: opacity => rgba(97, 86, 53, opacity),
      hoverContrast: opacity => rgba(0, 15, 51, opacity),
    },
  },
  border: {
    accent: {
      default: opacity => rgba(82, 95, 255, opacity),
    },
    complementary: (color, opacity) => borderComplementary[color](opacity),
    error: {
      default: opacity => rgba(255, 76, 76, opacity),
    },
    info: {
      default: opacity => rgba(255, 255, 255, opacity),
    },
    primary: {
      active: opacity => rgba(82, 95, 255, opacity),
      default: opacity => rgba(192, 194, 204, opacity),
      disabled: opacity => rgba(192, 194, 204, opacity),
      hover: opacity => rgba(105, 107, 116, opacity),
    },
    quaternary: {
      active: opacity => rgba(0, 15, 51, opacity),
      default: opacity => rgba(105, 107, 116, opacity),
      disabled: opacity => rgba(224, 226, 235, opacity),
      hover: opacity => rgba(0, 15, 51, opacity),
    },
    reversed: {
      active: opacity => rgba(255, 255, 255, opacity),
      default: opacity => rgba(255, 255, 255, opacity),
      disabled: opacity => rgba(192, 194, 204, opacity),
      hover: opacity => rgba(242, 244, 252, opacity),
    },
    secondary: {
      active: opacity => rgba(192, 194, 204, opacity),
      default: opacity => rgba(224, 226, 235, opacity),
      disabled: opacity => rgba(242, 244, 252, opacity),
      hover: opacity => rgba(192, 194, 204, opacity),
    },
    success: {
      default: opacity => rgba(20, 184, 125, opacity),
      contrast: opacity => rgba(17, 135, 88, opacity),
    },
    tertiary: {
      active: opacity => rgba(0, 15, 51, opacity),
      default: opacity => rgba(0, 15, 51, opacity),
      disabled: opacity => rgba(192, 194, 204, opacity),
      hover: opacity => rgba(105, 107, 116, opacity),
    },
    warning: {
      default: opacity => rgba(249, 178, 36, opacity),
    },
  },
  shadow: {
    primary: {
      default: opacity => rgba(0, 15, 51, opacity),
    },
    reversed: {
      default: opacity => rgba(255, 255, 255, opacity),
    },
    secondary: {
      default: opacity => rgba(192, 194, 204, opacity),
    },
    accent: {
      default: opacity => rgba(82, 95, 255, opacity),
    },
  },
};

export default color;
